import { useCallback, useEffect, useState } from 'react';

import { BankCodesForAll } from '@revenuemarket/bank';
import { Button, Flex, Text, Colors, Input } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { formatCompanyTaxNumber, deformatCompanyTaxNumber } from '@revenuemarket/utils';
import { Form, FormProps, ModalProps, Radio, Tooltip, Typography } from 'antd';
import { debounce } from 'lodash-es';
import * as yup from 'yup';

import { BusinessEntityType } from '~apis/business-entity';
import { BusinessEntityDetail } from '~apis/contract';
import BusinessEntityRepresentativeFormItems from '~components/BusinessEntity/BusinessEntityRepresentativeFormItems';
import CommonModal from '~components/ui/Modal/CommonModal';
import { useGetSimilarBusinessEntities } from '~hooks/queries/businessEntity/query';
import { useCreateBusinessEntity, useUpdateBusinessEntity } from '~hooks/queries/contract/query';
import useBusinessEntityListQuery from '~hooks/queries/useBusinessEntityListQuery';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';

import BusinessEntityDetailForm from './BusinessEntityDetailForm';
import IndividualBusinessForm from './IndividualBusinessForm';

interface Props extends ModalProps {
  name?: string;
  title?: string;
  onCreate?: (id: number) => void;
  onCancel?: () => void;
  data?: BusinessEntityDetail;
}

export const BusinessEntityCreateScheme = yup.object({
  name: yup.string().required('상호를 입력해주세요'),
  businessRegNo: yup.string().length(10, '사업자등록번호 10자리를 모두 입력해주세요'),
});

// name, email 모두 비어있는 row는 제거
const normalizeTaxInvoiceManagers = (managers?: { name: string; email: string }[]) => {
  return (managers ?? [])
    .map((m) => ({
      ...m,
      name: (m?.name ?? '').trim(),
      email: (m?.email ?? '').trim(),
    }))
    .filter((m) => m.name || m.email); // 둘 다 비어있는 row는 제거
};

interface BusinessEntityBankAccountFormType {
  id?: number;
  alias: string;
  organizationCode: BankCodesForAll;
  accountNumber: string;
  accountHolder: string | null;
}

const BusinessEntityCreateModal: React.FC<Props> = ({ title = '거래처 만들기', ...props }) => {
  const [representatives, setRepresentatives] = useState<{ id: number | null; name: string }[]>(
    props.data?.members.filter((m) => m.type === 'REPRESENTATIVE') || [{ id: null, name: '' }],
  );
  const { companyId } = useDemoAwareCompanyId();
  const { refetch: refetchBusinessEntity } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });
  const [businessType, setBusinessType] = useState<BusinessEntityType>(
    props.data?.type ?? BusinessEntityType.CORPORATION,
  );

  const [simulateData, setSimulateData] = useState<{ name: string; businessRegNo: string }>();
  const { mutateAsync: createBusinessEntity } = useCreateBusinessEntity();
  const { mutateAsync: updateBusinessEntity } = useUpdateBusinessEntity();
  const [form] = Form.useForm();

  const name = Form.useWatch('name', { form, preserve: true });
  const businessRegNo = Form.useWatch('businessRegNumber', { form, preserve: true });
  const isCardOrganization = props.data?.isCardOrganization;

  const { data } = useGetSimilarBusinessEntities({
    companyId,
    query: {
      name: simulateData?.name ?? '',
      businessRegNo: simulateData?.businessRegNo ?? '',
    },
  });

  // debounce 함수를 바깥에서 생성
  const debouncedUpdateSimulateData = useCallback(
    debounce((name, businessRegNo) => {
      setSimulateData((prev) => ({
        ...prev,
        name,
        businessRegNo,
      }));
    }, 500),
    [],
  );

  useEffect(() => {
    if (name || businessRegNo) {
      debouncedUpdateSimulateData(name, businessRegNo);
    }
  }, [name, businessRegNo, debouncedUpdateSimulateData]);

  const preview = data?.Data?.data;
  const previewList = preview?.similarList || [];

  const sameBusinessRegExist =
    previewList.some((item) => item.businessRegNumber === businessRegNo) &&
    previewList.find((item) => item.businessRegNumber === businessRegNo)?.id !== props.data?.id;

  const existingBusinessEntity = previewList.find(
    (item) => item.businessRegNumber === businessRegNo && item.id !== props.data?.id,
  );

  const onCreate = async (values: {
    name: string;
    alias: string;
    businessRegNumber: string;
    representativeNames: string[];
    address: string;
    btpNo: string;
    bzdNo: string;
    taxInvoiceManagers: { name: string; email: string }[];
    lotAddress: string;
    bankAccounts: BusinessEntityBankAccountFormType[];
    email: string;
    memo?: string;
  }) => {
    if (sameBusinessRegExist) {
      return;
    }

    try {
      await form.validateFields();
    } catch (error) {
      return;
    }

    // 둘 다 비어있는 row는 제거
    const invoiceManagers = normalizeTaxInvoiceManagers(values?.taxInvoiceManagers);

    const response = await createBusinessEntity({
      companyId,
      name: values.name,
      alias: values.alias || values.name,
      businessRegNumber: values?.businessRegNumber || null,
      representativeNames:
        businessType === BusinessEntityType.NATIONAL_INDIVIDUAL
          ? [values.name]
          : representatives.map((item) => item.name),
      address: values?.address ? `${values?.address} ${values?.lotAddress ?? ''}` : '',
      btpNo: values.btpNo,
      bzdNo: values.bzdNo,
      taxInvoiceManagers:
        businessType === BusinessEntityType.NATIONAL_INDIVIDUAL
          ? [{ name: values.name, email: values.email }]
          : invoiceManagers,
      type: businessType,
      bankAccounts:
        values?.bankAccounts?.map((item) => ({
          companyId,
          // 생성할때는 0으로 보내기
          businessEntityId: 0,
          organizationCode: item.organizationCode,
          alias: item.alias,
          accountNumber: item.accountNumber,
          accountHolder: item.accountHolder,
        })) || [],
      memo: values?.memo,
    });
    const data = response?.Data?.data;
    if (response.Status === ResponseStatus.SUCCESS && data !== undefined) {
      Toast.success('거래처가 성공적으로 생성되었습니다.');
      refetchBusinessEntity();
      props.onCreate?.(data.id);
      props.onCancel?.();
    } else {
      Toast.error('거래처 생성에 실패했습니다.');
    }
  };

  const onEdit = async (
    values: Partial<{
      name: string;
      alias: string;
      businessRegNumber: string;
      representativeNames: string[];
      address: string;
      btpNo: string;
      bzdNo: string;
      taxInvoiceManagers: { name: string; email: string }[];
      bankAccounts: BusinessEntityBankAccountFormType[];
      lotAddress: string;
      email: string;
      memo?: string;
    }>,
  ) => {
    if (sameBusinessRegExist) {
      return;
    }

    if (!props.data) return;

    const invoiceManagers =
      businessType === BusinessEntityType.NATIONAL_INDIVIDUAL
        ? [{ name: values.name || '', email: values.email || '' }]
        : normalizeTaxInvoiceManagers(values?.taxInvoiceManagers);

    const response = await updateBusinessEntity({
      companyId,
      request: {
        ...props.data,
        id: props.data.id,
        name: values?.name || '',
        alias: values.alias || values.name,
        representatives,
        businessRegNo: values?.businessRegNumber || null,
        address: values?.address ? `${values?.address} ${values?.lotAddress ?? ''}` : '',
        btpNo: values?.btpNo || '',
        bzdNo: values?.bzdNo || '',
        taxInvoiceManagers: invoiceManagers,
        type: businessType,
        memo: values?.memo ?? props.data?.memo,
        bankAccounts:
          values?.bankAccounts?.map((item) => ({
            id: item.id,
            companyId: companyId,
            businessEntityId: props.data!!.id,
            organizationCode: item.organizationCode,
            alias: item.alias,
            accountNumber: item.accountNumber,
            accountHolder: item?.accountHolder ?? null,
          })) || [],
      },
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('거래처가 성공적으로 수정되었습니다.');

      props.onCancel?.();
      props.onCreate?.(props.data.id);
    } else {
      Toast.error('거래처 수정에 실패했습니다.');
    }
  };

  useEffect(() => {
    if (props.data) {
      form.setFieldsValue({
        ...props.data,
        email:
          props.data?.type === BusinessEntityType.NATIONAL_INDIVIDUAL
            ? props.data?.taxInvoiceManagers[0]?.email
            : '',
        taxInvoiceManagers: props.data?.taxInvoiceManagers?.length
          ? props.data.taxInvoiceManagers
          : [],
        memo: props.data?.memo,
      });
    }
  }, [props.data]);

  // 에러 발생 시 첫 번째 필드로 스크롤
  const onFinishFailed: FormProps['onFinishFailed'] = ({ errorFields }) => {
    const first = errorFields?.[0];
    if (!first) return;

    form.scrollToField(first.name, { block: 'center' });

    const id = first.name.join('_');
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;

      if (typeof el.focus === 'function') {
        el.focus();
        return;
      }

      (el.querySelector('input,textarea,button') as HTMLElement | null)?.focus();
    }, 0);
  };

  return (
    <CommonModal
      centered
      open={props.open}
      onCancel={props.onCancel}
      okButtonProps={{
        disabled: data ? false : preview?.denyCreate,
      }}
      className={css`
        *::-webkit-scrollbar-track {
          background: transparent;
        }
      `}
      title={
        <Text
          textStyle="20_600"
          style={{
            color: Colors.gray900,
          }}
        >
          {title}
        </Text>
      }
      width={544}
      okText={props.data ? '수정하기' : '만들기'}
      onOk={() => {
        form.submit();
      }}
      style={{
        maxHeight: 'calc(100vh - 64px)',
      }}
      bodyStyle={{
        overflowY: 'scroll',
        maxHeight: 'calc(100vh - 180px)',
      }}
      footer={
        <Flex justify="flex-end" align="center" gap={8}>
          <Button onClick={props.onCancel}>닫기</Button>
          <Button
            buttonType="primary"
            type="submit"
            disabled={!name}
            onClick={() => {
              form.submit();
            }}
          >
            변경사항 저장
          </Button>
        </Flex>
      }
    >
      <div>
        {!isCardOrganization && (
          <Flex
            align="center"
            gap={16}
            style={{
              paddingTop: props.data ? 0 : 26,
            }}
          >
            <Radio.Group
              value={businessType}
              onChange={(e) => {
                setBusinessType(e.target.value);
                form.setFieldsValue({
                  businessRegNumber: '',
                });
              }}
              options={[
                {
                  value: BusinessEntityType.CORPORATION,
                  label: (
                    <Text
                      textStyle="14_400"
                      style={{
                        color: Colors.gray900,
                      }}
                    >
                      법인 또는 개인사업자
                    </Text>
                  ),
                },
                {
                  value: BusinessEntityType.NATIONAL_INDIVIDUAL,
                  label: (
                    <Text
                      textStyle="14_400"
                      style={{
                        color: Colors.gray900,
                      }}
                    >
                      사업자가 아닌 개인
                    </Text>
                  ),
                },
              ]}
              size="large"
            />
          </Flex>
        )}
        {businessType === BusinessEntityType.NATIONAL_INDIVIDUAL ? (
          <IndividualBusinessForm
            sameBusinessRegExist={sameBusinessRegExist}
            form={form}
            onFinish={props.data ? onEdit : onCreate}
          />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={props.data ? onEdit : onCreate}
            onFinishFailed={onFinishFailed}
            style={{
              marginTop: 26,
            }}
            validateTrigger="submit"
            initialValues={{
              taxInvoiceManagers: [],
            }}
          >
            <Flex gap={16}>
              <Flex direction="column" style={{ width: 400, flex: 3 }}>
                <Form.Item
                  required
                  name="name"
                  label={
                    <Flex align="center" gap={4}>
                      <Typography.Text
                        style={{
                          color: Colors.gray800,
                        }}
                      >
                        상호
                      </Typography.Text>
                    </Flex>
                  }
                >
                  <Input placeholder="세금계산서 발행에서 사용하는 상호를 입력해주세요" />
                </Form.Item>
                <Tooltip
                  title={isCardOrganization ? '카드사의 사업자등록번호를 변경할 수 없어요' : ''}
                  align={{
                    offset: [0, 16],
                  }}
                >
                  <Form.Item
                    name="businessRegNumber"
                    label={
                      <Flex align="center" gap={4}>
                        <Typography.Text
                          style={{
                            color: Colors.gray800,
                          }}
                        >
                          사업자등록번호
                        </Typography.Text>
                      </Flex>
                    }
                  >
                    <Input
                      placeholder="사업자등록번호 10자리"
                      formatter={formatCompanyTaxNumber}
                      deformatter={deformatCompanyTaxNumber}
                      inputStatus={sameBusinessRegExist ? 'error' : undefined}
                      additionalText={
                        sameBusinessRegExist
                          ? `이미 등록된 거래처입니다${
                              existingBusinessEntity?.alias
                                ? ` (${existingBusinessEntity.alias})`
                                : ''
                            }`
                          : ''
                      }
                      disabled={isCardOrganization}
                    />
                  </Form.Item>
                </Tooltip>
                <Form.Item
                  name="alias"
                  label={
                    <Flex align="center" gap={4}>
                      <Typography.Text
                        style={{
                          color: Colors.gray800,
                        }}
                      >
                        거래처명
                      </Typography.Text>
                    </Flex>
                  }
                >
                  <Input placeholder="이 거래처를 쉽게 알아볼 수 있도록 거래처 별칭을 입력해 주세요" />
                </Form.Item>
                <BusinessEntityRepresentativeFormItems
                  representatives={representatives}
                  onChange={setRepresentatives}
                />
                <BusinessEntityDetailForm form={form} />
              </Flex>
            </Flex>
          </Form>
        )}
      </div>
    </CommonModal>
  );
};

export default BusinessEntityCreateModal;
